import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Download, Monitor, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getLatestDesktopVersion, trackDesktopDownload } from '@/data';

export function Downloads() {
  const [latestVersion, setLatestVersion] = useState('1.0.0');
  const [downloadLinks, setDownloadLinks] = useState({
    windows: {}
  });
  const [changelog, setChangelog] = useState([]);
  const [releaseNotes, setReleaseNotes] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Fetch latest version from API
  useEffect(() => {
    const fetchLatestVersion = async () => {
      try {
        const response = await getLatestDesktopVersion();
        const data = response.data;
        
        if (data) {
          setLatestVersion(data.version);
          setDownloadLinks(data.downloads);
          setChangelog(data.changelog || []);
          setReleaseNotes(data.releaseNotes || '');
        }
      } catch (error) {
        console.error('Failed to fetch latest version:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLatestVersion();
  }, []);

  const handleDownload = async (url, versionId) => {
    // Convert relative URL to absolute if needed
    const downloadUrl = url.startsWith('http') 
      ? url 
      : `${process.env.REACT_APP_API_URL || 'http://localhost:36001'}${url}`;
    
    // Open download link
    window.open(downloadUrl, '_blank');
    
    // Track download analytics
    try {
      if (versionId) {
        await trackDesktopDownload(versionId);
      }
    } catch (error) {
      console.error('Failed to track download:', error);
    }
  };

  const features = [
    {
      icon: CheckCircle,
      text: 'Kolay Kurulum'
    },
    {
      icon: CheckCircle,
      text: 'Otomatik Güncellemeler'
    },
    {
      icon: CheckCircle,
      text: 'Offline Çalışma'
    },
    {
      icon: CheckCircle,
      text: 'Yazıcı Desteği'
    }
  ];

  const systemRequirements = {
    windows: [
      'Windows 10/11',
      '4 GB RAM',
      '500 MB disk alanı',
      'İnternet bağlantısı (kurulum için)'
    ]
  };

  return (
    <section id="downloads" className="relative py-20 px-4 overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 via-purple-900/30 to-slate-900/50"></div>
      
      <div className="container mx-auto max-w-7xl relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 bg-purple-500/10 border border-purple-500/30 rounded-full px-4 py-2 mb-4">
            <Download className="w-4 h-4 text-purple-400" />
            <span className="text-purple-300 text-sm font-medium">
              Desktop Uygulaması
            </span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent">
              EyayBooth'u
            </span>
            <span className="text-white"> İndir</span>
          </h2>
          
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Windows için optimize edilmiş masaüstü uygulamasını indirin.
            Son sürüm: <span className="text-purple-400 font-semibold">v{latestVersion}</span>
          </p>
        </motion.div>

        {/* Download Cards */}
        <div className="flex justify-center mb-16">
          {/* Windows Download Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="relative group max-w-md w-full"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-300"></div>
            
            <div className="relative bg-slate-900/90 backdrop-blur-sm border border-gray-800 rounded-3xl p-8 hover:border-cyan-500/50 transition-all duration-300">
              {/* Platform Icon */}
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Monitor className="w-10 h-10 text-white" />
              </div>

              <h3 className="text-2xl font-bold text-white mb-2">Windows</h3>
              <p className="text-gray-400 mb-6">Windows 10/11 için</p>

              {/* Download Buttons */}
              <div className="space-y-3 mb-6">
                {downloadLinks.windows.installer && (
                  <Button
                    onClick={() => handleDownload(downloadLinks.windows.installer.url)}
                    className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white rounded-xl group/btn"
                    disabled={isLoading}
                  >
                    <Download className="w-4 h-4 mr-2 group-hover/btn:animate-bounce" />
                    Kurulum İndir (Önerilen)
                  </Button>
                )}
                
                {downloadLinks.windows.portable && (
                  <Button
                    onClick={() => handleDownload(downloadLinks.windows.portable.url)}
                    variant="outline"
                    className="w-full border-cyan-500/50 text-cyan-300 hover:bg-cyan-500/10 rounded-xl"
                    disabled={isLoading}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Portable İndir
                  </Button>
                )}
                
                {!downloadLinks.windows.installer && !downloadLinks.windows.portable && !isLoading && (
                  <p className="text-sm text-gray-400 text-center">Henüz Windows versiyonu mevcut değil</p>
                )}
              </div>

              {/* System Requirements */}
              <div className="border-t border-gray-800 pt-4">
                <p className="text-sm font-semibold text-gray-400 mb-3">Sistem Gereksinimleri:</p>
                <ul className="space-y-2">
                  {systemRequirements.windows.map((req, index) => (
                    <li key={index} className="text-sm text-gray-500 flex items-start gap-2">
                      <span className="text-cyan-400 mt-0.5">•</span>
                      {req}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Features Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12"
        >
          {features.map((feature, index) => (
            <div
              key={index}
              className="flex items-center gap-3 bg-slate-900/50 backdrop-blur-sm border border-gray-800 rounded-xl p-4"
            >
              <feature.icon className="w-5 h-5 text-green-400 flex-shrink-0" />
              <span className="text-sm text-gray-300">{feature.text}</span>
            </div>
          ))}
        </motion.div>

        {/* Installation Help */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-gradient-to-r from-purple-500/10 via-pink-500/10 to-cyan-500/10 border border-purple-500/30 rounded-2xl p-6"
        >
          <div className="flex items-start gap-4">
            <AlertCircle className="w-6 h-6 text-purple-400 flex-shrink-0 mt-1" />
            <div>
              <h4 className="text-lg font-semibold text-white mb-2">
                Yardıma mı ihtiyacınız var?
              </h4>
              <p className="text-gray-300 mb-4">
                Kurulum veya kullanım ile ilgili sorunlarınız için detaylı dokümantasyonumuza göz atın 
                veya destek ekibimizle iletişime geçin.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button
                  variant="outline"
                  className="border-purple-500/50 text-purple-300 hover:bg-purple-500/10"
                  onClick={() => window.open('/docs/installation', '_blank')}
                >
                  Kurulum Kılavuzu
                </Button>
                <Button
                  variant="outline"
                  className="border-purple-500/50 text-purple-300 hover:bg-purple-500/10"
                  onClick={() => window.open('/support', '_blank')}
                >
                  Destek Al
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

